import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Send, CornerDownLeft, Home, Mic, Volume2 } from 'lucide-react';
import { AudioRecorder } from '@/components/triage/AudioRecorder';
import { transcribeAudio } from '@/services/transcriptionService';
import { useAudioTriage } from '@/contexts/AudioTriageContext';

export const AudioSymptomCollector: React.FC = () => {
  const navigate = useNavigate();
  const { state, setSymptoms } = useAudioTriage();
  
  const [symptomsText, setSymptomsText] = useState(state.symptomsText || '');
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(state.recordedBlob);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [customSymptom, setCustomSymptom] = useState('');
  const [isRecording, setIsRecording] = useState(false);

  // Get symptoms from context
  const symptoms = state.selectedSymptoms;

  // Predefined symptoms for selection
  const predefinedSymptoms = [
    'Fever', 'Cough', 'Headache', 'Fatigue', 'Nausea', 'Dizziness',
    'Chest Pain', 'Shortness of Breath', 'Abdominal Pain', 'Back Pain',
    'Joint Pain', 'Sore Throat', 'Runny Nose', 'Skin Rash', 'Vomiting', 'Diarrhea'
  ];

  const handleRecordingComplete = async (blob: Blob | null) => {
    setRecordedBlob(blob);
    if (!blob) return;

    try {
      setIsTranscribing(true);
      const text = await transcribeAudio(blob);
      // Append transcription if textarea already has content
      setSymptomsText((prev) => (prev.trim() ? `${prev}\n${text}` : text));
    } catch (err) {
      console.error('Transcription error', err);
      alert('Sorry, we could not transcribe your recording.');
    } finally {
      setIsTranscribing(false);
    }
  };

  // Add custom symptom
  const addCustomSymptom = () => {
    if (customSymptom.trim() && !symptoms.includes(customSymptom.trim())) {
      setSymptoms([...symptoms, customSymptom.trim()], symptomsText, recordedBlob, null);
      setCustomSymptom('');
    }
  };

  // Toggle symptom selection
  const toggleSymptom = (symptom: string) => {
    const newSymptoms = symptoms.includes(symptom) 
      ? symptoms.filter(s => s !== symptom)
      : [...symptoms, symptom];
    setSymptoms(newSymptoms, symptomsText, recordedBlob, null);
  };

  // Remove selected symptom
  const removeSymptom = (symptom: string) => {
    const newSymptoms = symptoms.filter(s => s !== symptom);
    setSymptoms(newSymptoms, symptomsText, recordedBlob, null);
  };

  // Voice recording simulation
  const toggleRecording = () => {
    setIsRecording(!isRecording);
    // Here you would integrate with actual voice recording
  };

  const handleProceed = () => {
    if (symptoms.length > 0 || symptomsText.trim() || recordedBlob) {
      // Update context with all symptom data
      setSymptoms(symptoms, symptomsText, recordedBlob, null);
      console.log('Proceeding with symptoms:', symptoms, symptomsText, recordedBlob);
      navigate('/patient/audio-triage/question');
    } else {
      alert('Please select symptoms, describe your symptoms, or record your voice.');
    }
  };

  const handleGoBack = () => {
    navigate('/patient/audio-triage/welcome');
  };

  const handleBackToDashboard = () => {
    navigate('/patient/dashboard');
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 p-4 sm:p-6 pt-12">
      
      <div className="w-full max-w-4xl bg-white p-6 sm:p-8 rounded-xl shadow-2xl">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 text-center mb-6">Select Your Symptoms</h1>
        
        <div className="space-y-6">
          {/* Symptom Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                What symptoms are you experiencing?
                <Button variant="ghost" size="sm">
                  <Volume2 className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Predefined Symptoms Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {predefinedSymptoms.map((symptom) => (
                  <Button
                    key={symptom}
                    variant={symptoms.includes(symptom) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleSymptom(symptom)}
                    className="justify-start"
                  >
                    {symptom}
                  </Button>
                ))}
              </div>

              {/* Custom Symptom Input */}
              <div className="flex gap-2">
                <Input
                  placeholder="Describe other symptoms..."
                  value={customSymptom}
                  onChange={(e) => setCustomSymptom(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addCustomSymptom()}
                />
                <Button
                  variant={isRecording ? "destructive" : "outline"}
                  size="sm"
                  onClick={toggleRecording}
                >
                  <Mic className={`h-4 w-4 ${isRecording ? 'animate-pulse' : ''}`} />
                </Button>
                <Button onClick={addCustomSymptom} size="sm">
                  + Add
                </Button>
              </div>

              {/* Selected Symptoms */}
              {symptoms.length > 0 && (
                <div className="space-y-2">
                  <Label>Selected symptoms:</Label>
                  <div className="flex flex-wrap gap-2">
                    {symptoms.map((symptom) => (
                      <Badge key={symptom} variant="secondary" className="flex items-center gap-1">
                        {symptom}
                        <button onClick={() => removeSymptom(symptom)} className="ml-1 text-xs">×</button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Audio Recording Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Or describe your symptoms with voice
                <Button variant="ghost" size="sm">
                  <Mic className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="my-6 flex flex-col items-center gap-3 w-full max-w-md mx-auto">
                <AudioRecorder onRecordingComplete={handleRecordingComplete} />
                {isTranscribing && (
                  <p className="text-sm text-blue-700 bg-blue-100 border border-blue-300 px-3 py-2 rounded-md w-full text-center animate-pulse">
                    Transcribing your recording...
                  </p>
                )}
                {!isTranscribing && recordedBlob && (
                  <p className="text-sm text-green-700 bg-green-100 border border-green-300 px-3 py-2 rounded-md w-full text-center">
                    Recording complete. You can record again, edit the text below, or proceed.
                  </p>
                )}
              </div>

              <div className="mb-6">
                <label htmlFor="symptomsText" className="block text-sm font-medium text-slate-700 mb-1.5">Or type your symptoms here:</label>
                <Textarea
                  id="symptomsText"
                  value={symptomsText}
                  onChange={(e) => setSymptomsText(e.target.value)}
                  placeholder="e.g., I have a sore throat and a slight fever..."
                  rows={5}
                  className="border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 rounded-lg shadow-sm w-full"
                  aria-label="Text area to type symptoms"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          <Button 
            variant="outline"
            onClick={handleGoBack} 
            className="w-full sm:w-auto flex items-center justify-center border-slate-300 text-slate-700 hover:bg-slate-100 focus-visible:ring-indigo-500 rounded-lg py-3 sm:py-2.5"
            aria-label="Go back to the welcome screen"
          >
            <CornerDownLeft size={18} className="mr-2" /> Go Back
          </Button>
          <Button 
            variant="outline"
            onClick={handleBackToDashboard} 
            className="w-full sm:w-auto flex items-center justify-center border-slate-300 text-slate-700 hover:bg-slate-100 focus-visible:ring-indigo-500 rounded-lg py-3 sm:py-2.5"
            aria-label="Return to dashboard"
          >
            <Home size={18} className="mr-2" /> Dashboard
          </Button>
          <Button 
            onClick={handleProceed} 
            disabled={symptoms.length === 0 && symptomsText.trim() === '' && !recordedBlob}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed flex-grow rounded-lg py-3 sm:py-2.5"
            aria-label={symptoms.length === 0 && symptomsText.trim() === '' && !recordedBlob ? "Please select or describe symptoms to proceed" : "Proceed to the next step with entered symptoms"}
          >
            <Send size={18} className="mr-2" /> Next
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-8 text-center max-w-md mx-auto">
        The information you provide will help us guide you to the appropriate care. 
      </p>
    </div>
  );
};
